import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Business,
  People,
  AccountTree,
  Receipt,
  History,
} from '@mui/icons-material';
import SectionHeader from './shared/SectionHeader';
import { adminService, authService, deviceService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { tokens } from '../theme';

// ─── Sección visual ──────────────────────────────────────────────────────────
const Section = ({ icon: Icon, title, description, children, restricted, sx }) => (
  <Card sx={sx}>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: tokens.radius.md,
            backgroundColor: tokens.color.brand[50],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: tokens.color.brand[500],
            flexShrink: 0,
            '& .MuiSvgIcon-root': { fontSize: 18 },
          }}
        >
          <Icon />
        </Box>
        <Box>
          <Typography variant="h5">{title}</Typography>
          {description && (
            <Typography variant="body2" sx={{ color: tokens.color.ink.muted, mt: 0.25 }}>
              {description}
            </Typography>
          )}
        </Box>
      </Box>

      {restricted ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Solo <strong>Master Admin</strong> puede gestionar esta sección.
        </Alert>
      ) : null}

      {children}
    </CardContent>
  </Card>
);

const asSelectValue = (value) => (value === null || value === undefined ? '' : String(value));

// ─── SaaSManagement ──────────────────────────────────────────────────────────
const SaaSManagement = () => {
  const { user } = useAuth();
  const isMasterAdmin = user?.role === 'master_admin';

  const [projects,       setProjects]       = useState([]);
  const [devices,        setDevices]        = useState([]);
  const [endUsers,       setEndUsers]       = useState([]);
  const [admins,         setAdmins]         = useState([]);
  const [projectAdmins,  setProjectAdmins]  = useState([]);
  const [billingHistory, setBillingHistory] = useState([]);
  const [history,        setHistory]        = useState(null);

  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedDeviceId,  setSelectedDeviceId]  = useState('');
  const [selectedAdminId,   setSelectedAdminId]   = useState('');
  const [selectedEndUserId, setSelectedEndUserId] = useState('');
  const [endUserSearch,     setEndUserSearch]     = useState('');
  const [activeTab, setActiveTab] = useState('assignments');

  const [newProject, setNewProject]     = useState({ name: '', code: '', description: '' });
  const [profilePatch, setProfilePatch] = useState({
    profile_name: '', profile_notes: '', current_end_user_name: '', current_end_user_reference: '',
  });
  const [billingPatch, setBillingPatch] = useState({
    billing_status: 'active', reason: '', next_billing_date: '',
  });
  const [paymentPatch, setPaymentPatch] = useState({
    amount: '', currency: 'USD', notes: '', auto_activate: true,
  });
  const [newEndUser, setNewEndUser] = useState({
    full_name: '', external_reference: '', phone: '', email: '', notes: '',
  });
  const [passwordPatch, setPasswordPatch] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [newAdminUser, setNewAdminUser] = useState({ username: '', email: '', password: '', role: 'admin' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [creatingAdmin, setCreatingAdmin] = useState(false);

  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const selectedProject = useMemo(
    () => projects.find((p) => asSelectValue(p.id) === selectedProjectId),
    [projects, selectedProjectId]
  );
  const selectedDevice = useMemo(
    () => devices.find((d) => asSelectValue(d.id) === selectedDeviceId),
    [devices, selectedDeviceId]
  );

  const showSnack = (message, severity = 'success') => setSnack({ open: true, message, severity });

  const bootstrap = async () => {
    try {
      const [projRes, devRes, admRes, euRes] = await Promise.all([
        adminService.getProjects(),
        deviceService.getDevices(),
        adminService.getAdmins().catch(() => ({ success: false, admins: [] })),
        adminService.getEndUsers().catch(() => ({ success: false, end_users: [] })),
      ]);
      if (projRes.success) {
        setProjects(projRes.projects || []);
        if (!selectedProjectId && projRes.projects?.length) {
          setSelectedProjectId(asSelectValue(projRes.projects[0].id));
        }
      }
      if (devRes.success)  setDevices(devRes.devices || []);
      if (admRes.success)  setAdmins(admRes.admins || []);
      if (euRes.success)   setEndUsers(euRes.end_users || []);
    } catch {
      showSnack('No se pudo cargar la vista SaaS', 'error');
    }
  };

  useEffect(() => { bootstrap(); }, []);

  useEffect(() => {
    if (selectedProjectId) {
      adminService.getProjectAdmins(selectedProjectId)
        .then((r) => { if (r.success) setProjectAdmins(r.admins || []); })
        .catch(() => setProjectAdmins([]));
      adminService.getProjectBillingHistory(selectedProjectId)
        .then((r) => { if (r.success) setBillingHistory(r.events || []); })
        .catch(() => {});
    }
  }, [selectedProjectId]);

  useEffect(() => {
    if (selectedProject) {
      setBillingPatch((p) => ({
        ...p,
        billing_status: selectedProject.billing_status || 'active',
        next_billing_date: selectedProject.next_billing_date || '',
      }));
    }
  }, [selectedProject]);

  useEffect(() => {
    if (selectedDevice) setSelectedEndUserId(asSelectValue(selectedDevice.current_end_user_id));
    else setSelectedEndUserId('');
  }, [selectedDevice]);

  useEffect(() => {
    adminService.getEndUsers(endUserSearch)
      .then((r) => { if (r.success) setEndUsers(r.end_users || []); })
      .catch(() => {});
  }, [endUserSearch]);

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const wrap = (fn, successMsg) => async () => {
    try {
      const res = await fn();
      if (res?.success) { showSnack(successMsg); await bootstrap(); }
    } catch (e) {
      showSnack(e.response?.data?.error || 'Error al procesar la solicitud', 'error');
    }
  };

  const handleAssignDevice = wrap(
    () => adminService.reassignDeviceProject(selectedDeviceId, selectedProjectId),
    'Tablet reasignada correctamente'
  );

  const handleSaveProfile = wrap(
    () => adminService.updateDeviceProfile(selectedDeviceId, profilePatch),
    'Perfil de tablet actualizado'
  );

  const handleAssignAdmin = wrap(
    async () => {
      const res = await adminService.assignAdminToProject(selectedProjectId, selectedAdminId);
      setSelectedAdminId('');
      return res;
    },
    'Admin asignado al proyecto'
  );

  const handleRemoveAdmin = (adminId) => wrap(
    () => adminService.removeAdminFromProject(selectedProjectId, adminId),
    'Admin desasignado del proyecto'
  )();

  const handleCreateProject = async () => {
    const code = newProject.code.trim().toUpperCase();
    if (!newProject.name || !code) {
      showSnack('Nombre y código son obligatorios', 'error');
      return;
    }
    if (!/^[A-Z0-9_-]{3,30}$/.test(code)) {
      showSnack('Código inválido. Usa 3-30 caracteres (A-Z, 0-9, _, -)', 'error');
      return;
    }
    try {
      const res = await adminService.createProject({ ...newProject, code });
      if (res.success) {
        showSnack('Proyecto creado correctamente');
        setNewProject({ name: '', code: '', description: '' });
        await bootstrap();
      }
    } catch (e) {
      showSnack(e.response?.data?.error || 'No se pudo crear el proyecto', 'error');
    }
  };

  const handleUpdateBilling = async () => {
    try {
      const res = await adminService.updateProjectBillingStatus(
        selectedProjectId,
        billingPatch.billing_status,
        billingPatch.reason,
        billingPatch.next_billing_date || null
      );
      if (res.success) {
        showSnack('Estado de facturación actualizado');
        await bootstrap();
      }
    } catch (e) {
      showSnack(e.response?.data?.error || 'No se pudo actualizar facturación', 'error');
    }
  };

  const handleRegisterPayment = async () => {
    const amount = Number(paymentPatch.amount);
    if (isNaN(amount) || amount <= 0) {
      showSnack('El monto debe ser mayor que 0', 'error');
      return;
    }
    try {
      const res = await adminService.registerProjectPayment(selectedProjectId, {
        amount,
        currency: paymentPatch.currency.trim().toUpperCase(),
        notes: paymentPatch.notes || null,
        auto_activate: paymentPatch.auto_activate,
      });
      if (res.success) {
        showSnack('Pago registrado correctamente');
        setPaymentPatch({ amount: '', currency: 'USD', notes: '', auto_activate: true });
        await bootstrap();
      }
    } catch (e) {
      showSnack(e.response?.data?.error || 'No se pudo registrar el pago', 'error');
    }
  };

  const handleCreateEndUser = async () => {
    if (!newEndUser.full_name?.trim() || newEndUser.full_name.trim().length < 2) {
      showSnack('El nombre debe tener al menos 2 caracteres', 'error');
      return;
    }
    try {
      const res = await adminService.createEndUser({
        full_name: newEndUser.full_name.trim(),
        external_reference: newEndUser.external_reference.trim() || null,
        phone: newEndUser.phone.trim() || null,
        email: newEndUser.email.trim() || null,
        notes: newEndUser.notes.trim() || null,
      });
      if (res.success) {
        showSnack('Usuario final creado');
        setNewEndUser({ full_name: '', external_reference: '', phone: '', email: '', notes: '' });
        await adminService.getEndUsers('').then((r) => { if (r.success) setEndUsers(r.end_users || []); });
      }
    } catch (e) {
      showSnack(e.response?.data?.error || 'No se pudo crear el usuario', 'error');
    }
  };

  const handleChangeOwnPassword = async () => {
    if (!passwordPatch.currentPassword || !passwordPatch.newPassword) {
      showSnack('Completa contraseña actual y nueva contraseña', 'error');
      return;
    }
    if (passwordPatch.newPassword.length < 8) {
      showSnack('La nueva contraseña debe tener al menos 8 caracteres', 'error');
      return;
    }
    if (passwordPatch.newPassword !== passwordPatch.confirmPassword) {
      showSnack('La confirmación no coincide con la nueva contraseña', 'error');
      return;
    }

    setChangingPassword(true);
    try {
      const response = await authService.changePassword(passwordPatch.currentPassword, passwordPatch.newPassword);
      if (response.success) {
        showSnack('Contraseña actualizada correctamente');
        setPasswordPatch({ currentPassword: '', newPassword: '', confirmPassword: '' });
      }
    } catch (error) {
      showSnack(error.response?.data?.error || 'No se pudo cambiar la contraseña', 'error');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleCreateAdminAccount = async () => {
    if (!isMasterAdmin) {
      showSnack('Solo Master Admin puede crear cuentas', 'error');
      return;
    }

    const username = newAdminUser.username.trim();
    const email = newAdminUser.email.trim().toLowerCase();

    if (!username || !email || !newAdminUser.password) {
      showSnack('Completa usuario, email y contraseña', 'error');
      return;
    }
    if (newAdminUser.password.length < 8) {
      showSnack('La contraseña debe tener al menos 8 caracteres', 'error');
      return;
    }

    setCreatingAdmin(true);
    try {
      const response = await adminService.createAdmin({
        username,
        email,
        password: newAdminUser.password,
        role: newAdminUser.role,
      });

      if (response.success) {
        showSnack('Cuenta administrativa creada');
        setNewAdminUser({ username: '', email: '', password: '', role: 'admin' });
        await bootstrap();
      }
    } catch (error) {
      showSnack(error.response?.data?.error || 'No se pudo crear la cuenta', 'error');
    } finally {
      setCreatingAdmin(false);
    }
  };

  const handleLinkEndUser = async (overrideId) => {
    if (!selectedDeviceId) { showSnack('Selecciona una tablet', 'error'); return; }
    const targetId = overrideId !== undefined ? overrideId : selectedEndUserId;
    try {
      const res = await adminService.linkDeviceEndUser(selectedDeviceId, targetId || null);
      if (res.success) { showSnack(res.message || 'Vinculación actualizada'); await bootstrap(); }
    } catch (e) {
      showSnack(e.response?.data?.error || 'No se pudo actualizar la vinculación', 'error');
    }
  };

  const handleLoadHistory = async () => {
    if (!selectedDeviceId) return;
    try {
      const res = await adminService.getDeviceHistory(selectedDeviceId);
      if (res.success) setHistory(res.history);
    } catch {
      showSnack('No se pudo cargar el historial', 'error');
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  const projectSelector = (
    <TextField
      select fullWidth label="Proyecto"
      value={selectedProjectId}
      onChange={(e) => setSelectedProjectId(asSelectValue(e.target.value))}
    >
      {projects.map((p) => (
        <MenuItem key={p.id} value={asSelectValue(p.id)}>
          {p.name}
          <Typography component="span" variant="caption" sx={{ ml: 1, color: tokens.color.ink.muted }}>
            {p.code}
          </Typography>
        </MenuItem>
      ))}
    </TextField>
  );

  const deviceSelector = (label = 'Tablet') => (
    <TextField
      select fullWidth label={label}
      value={selectedDeviceId}
      onChange={(e) => setSelectedDeviceId(asSelectValue(e.target.value))}
    >
      {devices.map((d) => (
        <MenuItem key={d.id} value={asSelectValue(d.id)}>
          {d.device_name || d.device_id}
        </MenuItem>
      ))}
    </TextField>
  );

  const tabSubtitle = {
    assignments: 'Asignaciones, perfil de tablet y usuarios finales',
    admins: 'Administradores por proyecto y control de acceso',
    projects: 'Alta de proyectos y estructura SaaS',
    billing: 'Estado de facturacion y registro de pagos',
    history: 'Historial consolidado de cambios por tablet',
  };

  return (
    <Box>
      <SectionHeader
        title="Gestión SaaS"
        subtitle={tabSubtitle[activeTab]}
        sx={{ mb: 2 }}
      />

      <Card sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 1.5, pt: 0.5 }}
        >
          <Tab value="assignments" label="Asignaciones" />
          <Tab value="admins" label="Admins" />
          <Tab value="projects" label="Proyectos" />
          <Tab value="billing" label="Facturacion" />
          <Tab value="history" label="Historial" />
        </Tabs>
      </Card>

      <Stack spacing={2}>
        {activeTab === 'assignments' && (
          <>
        {/* 1. Asignación de tablet a proyecto */}
        <Section icon={AccountTree} title="Asignar tablet a proyecto">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>{deviceSelector()}</Grid>
            <Grid size={{ xs: 12, md: 6 }}>{projectSelector}</Grid>
            <Grid size={12}>
              <Button
                variant="contained"
                onClick={handleAssignDevice}
                disabled={!selectedDeviceId || !selectedProjectId}
              >
                Asignar tablet al proyecto
              </Button>
            </Grid>
          </Grid>
        </Section>

        {/* 2. Perfil de tablet */}
        <Section icon={Business} title="Perfil de tablet" description="Nombre, usuario asignado y notas">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>{deviceSelector('Seleccionar tablet')}</Grid>
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => {
                  if (!selectedDevice) return;
                  setProfilePatch({
                    profile_name: selectedDevice.profile_name || '',
                    profile_notes: selectedDevice.profile_notes || '',
                    current_end_user_name: selectedDevice.current_end_user_name || '',
                    current_end_user_reference: selectedDevice.current_end_user_reference || '',
                  });
                }}
                disabled={!selectedDeviceId}
              >
                Cargar datos actuales
              </Button>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth label="Nombre de perfil"
                value={profilePatch.profile_name}
                onChange={(e) => setProfilePatch((p) => ({ ...p, profile_name: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth label="Nombre del usuario final"
                value={profilePatch.current_end_user_name}
                onChange={(e) => setProfilePatch((p) => ({ ...p, current_end_user_name: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth label="Referencia del usuario final"
                value={profilePatch.current_end_user_reference}
                onChange={(e) => setProfilePatch((p) => ({ ...p, current_end_user_reference: e.target.value }))}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth multiline rows={2} label="Notas"
                value={profilePatch.profile_notes}
                onChange={(e) => setProfilePatch((p) => ({ ...p, profile_notes: e.target.value }))}
              />
            </Grid>
            <Grid size={12}>
              <Button variant="contained" onClick={handleSaveProfile} disabled={!selectedDeviceId}>
                Guardar perfil
              </Button>
            </Grid>
          </Grid>
        </Section>

        {/* 3. Vinculación tablet - usuario final */}
        <Section icon={People} title="Vincular tablet a usuario final">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>{deviceSelector()}</Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth label="Buscar usuario final"
                value={endUserSearch}
                onChange={(e) => setEndUserSearch(e.target.value)}
                placeholder="Nombre o referencia..."
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                select fullWidth label="Usuario final"
                value={selectedEndUserId}
                onChange={(e) => setSelectedEndUserId(asSelectValue(e.target.value))}
              >
                <MenuItem value="">Sin vinculación</MenuItem>
                {endUsers.map((u) => (
                  <MenuItem key={u.id} value={asSelectValue(u.id)}>
                    {u.full_name}
                    {u.external_reference && (
                      <Typography component="span" variant="caption" sx={{ ml: 1, color: tokens.color.ink.muted }}>
                        ({u.external_reference})
                      </Typography>
                    )}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={12}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button variant="contained" onClick={() => handleLinkEndUser()} disabled={!selectedDeviceId}>
                  Guardar vinculación
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => { setSelectedEndUserId(''); handleLinkEndUser(null); }}
                  disabled={!selectedDeviceId}
                >
                  Desvincular
                </Button>
              </Stack>
            </Grid>

            {selectedDevice && (
              <Grid size={12}>
                <Alert severity="info" sx={{ mt: 0.5 }}>
                  <strong>{selectedDevice.device_name || selectedDevice.device_id}</strong>
                  {' → '}
                  {selectedDevice.current_end_user_name || 'Sin usuario vinculado'}
                </Alert>
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 2.5 }} />

          <Typography variant="h6" sx={{ mb: 2 }}>Crear nuevo usuario final</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth label="Nombre completo *"
                value={newEndUser.full_name}
                onChange={(e) => setNewEndUser((p) => ({ ...p, full_name: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth label="Referencia externa"
                value={newEndUser.external_reference}
                onChange={(e) => setNewEndUser((p) => ({ ...p, external_reference: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth label="Teléfono"
                value={newEndUser.phone}
                onChange={(e) => setNewEndUser((p) => ({ ...p, phone: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth label="Email"
                type="email"
                value={newEndUser.email}
                onChange={(e) => setNewEndUser((p) => ({ ...p, email: e.target.value }))}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth label="Notas"
                value={newEndUser.notes}
                onChange={(e) => setNewEndUser((p) => ({ ...p, notes: e.target.value }))}
              />
            </Grid>
            <Grid size={12}>
              <Button variant="outlined" onClick={handleCreateEndUser}>
                Crear usuario final
              </Button>
            </Grid>
          </Grid>

          {devices.length > 0 && (
            <>
              <Divider sx={{ my: 2.5 }} />
              <Typography variant="overline" sx={{ mb: 1.5, display: 'block' }}>
                Mapa tablet → usuario
              </Typography>
              <Stack spacing={1}>
                {devices.slice(0, 20).map((d) => (
                  <Box
                    key={d.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1.25,
                      borderRadius: tokens.radius.md,
                      border: `1px solid ${tokens.color.border.default}`,
                      backgroundColor: tokens.color.surface.default,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {d.device_name || d.device_id}
                    </Typography>
                    <Typography variant="caption" sx={{ color: tokens.color.ink.muted }}>
                      {d.current_end_user_name || 'Sin usuario'}
                      {d.current_end_user_reference ? ` · ${d.current_end_user_reference}` : ''}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </>
          )}
        </Section>
          </>
        )}

        {activeTab === 'admins' && (
          <>
        {/* 4. Multi-admin por proyecto */}
        <Section
          icon={People}
          title="Administradores por proyecto"
          restricted={!isMasterAdmin}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>{projectSelector}</Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select fullWidth label="Admin a asignar"
                value={selectedAdminId}
                onChange={(e) => setSelectedAdminId(asSelectValue(e.target.value))}
                disabled={!isMasterAdmin}
              >
                {admins.map((a) => (
                  <MenuItem key={a.id} value={asSelectValue(a.id)}>
                    {a.username}
                    <Typography component="span" variant="caption" sx={{ ml: 1, color: tokens.color.ink.muted }}>
                      {a.role}
                    </Typography>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={12}>
              <Button
                variant="contained"
                onClick={handleAssignAdmin}
                disabled={!isMasterAdmin || !selectedAdminId || !selectedProjectId}
              >
                Asignar admin
              </Button>
            </Grid>
            {projectAdmins.length > 0 && (
              <Grid size={12}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {projectAdmins.map((a) => (
                    <Chip
                      key={a.id}
                      label={`${a.username} · ${a.role}`}
                      onDelete={isMasterAdmin ? () => handleRemoveAdmin(a.id) : undefined}
                      size="small"
                      sx={{
                        backgroundColor: tokens.color.brand[50],
                        color: tokens.color.brand[600],
                        border: `1px solid ${tokens.color.brand[100]}`,
                        fontWeight: 500,
                      }}
                    />
                  ))}
                </Stack>
              </Grid>
            )}
          </Grid>
        </Section>

        <Section icon={Business} title="Seguridad de cuenta" description="Cambia tu contraseña desde el panel">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="password"
                label="Contraseña actual"
                value={passwordPatch.currentPassword}
                onChange={(e) => setPasswordPatch((prev) => ({ ...prev, currentPassword: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="password"
                label="Nueva contraseña"
                value={passwordPatch.newPassword}
                onChange={(e) => setPasswordPatch((prev) => ({ ...prev, newPassword: e.target.value }))}
                helperText="Mínimo 8 caracteres"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="password"
                label="Confirmar nueva contraseña"
                value={passwordPatch.confirmPassword}
                onChange={(e) => setPasswordPatch((prev) => ({ ...prev, confirmPassword: e.target.value }))}
              />
            </Grid>
            <Grid size={12}>
              <Button variant="contained" onClick={handleChangeOwnPassword} disabled={changingPassword}>
                {changingPassword ? 'Actualizando...' : 'Actualizar contraseña'}
              </Button>
            </Grid>
          </Grid>

          {isMasterAdmin && (
            <>
              <Divider sx={{ my: 2.5 }} />
              <Typography variant="h6" sx={{ mb: 2 }}>Crear nueva cuenta administrativa</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    label="Usuario"
                    value={newAdminUser.username}
                    onChange={(e) => setNewAdminUser((prev) => ({ ...prev, username: e.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="email"
                    label="Email"
                    value={newAdminUser.email}
                    onChange={(e) => setNewAdminUser((prev) => ({ ...prev, email: e.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Contraseña temporal"
                    value={newAdminUser.password}
                    onChange={(e) => setNewAdminUser((prev) => ({ ...prev, password: e.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 2 }}>
                  <TextField
                    select
                    fullWidth
                    label="Rol"
                    value={newAdminUser.role}
                    onChange={(e) => setNewAdminUser((prev) => ({ ...prev, role: e.target.value }))}
                  >
                    <MenuItem value="master_admin">Master Admin</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="viewer">Viewer</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={12}>
                  <Button variant="outlined" onClick={handleCreateAdminAccount} disabled={creatingAdmin}>
                    {creatingAdmin ? 'Creando cuenta...' : 'Crear cuenta admin'}
                  </Button>
                </Grid>
              </Grid>
            </>
          )}
        </Section>
          </>
        )}

        {activeTab === 'projects' && (
          <>
        {/* 5. Crear proyecto */}
        <Section
          icon={AccountTree}
          title="Crear proyecto"
          restricted={!isMasterAdmin}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth label="Nombre del proyecto"
                value={newProject.name}
                onChange={(e) => setNewProject((p) => ({ ...p, name: e.target.value }))}
                disabled={!isMasterAdmin}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth label="Código (ej: SALUD_DR)"
                value={newProject.code}
                onChange={(e) => setNewProject((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
                disabled={!isMasterAdmin}
                helperText="3-30 caracteres, solo A-Z 0-9 _ -"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <TextField
                fullWidth label="Descripción"
                value={newProject.description}
                onChange={(e) => setNewProject((p) => ({ ...p, description: e.target.value }))}
                disabled={!isMasterAdmin}
              />
            </Grid>
            <Grid size={12}>
              <Button
                variant="contained"
                onClick={handleCreateProject}
                disabled={!isMasterAdmin || !newProject.name || !newProject.code}
              >
                Crear proyecto
              </Button>
            </Grid>
          </Grid>
        </Section>
          </>
        )}

        {activeTab === 'billing' && (
          <>
        {/* 6. Facturación */}
        <Section
          icon={Receipt}
          title="Facturación y pagos"
          description="Estado de facturación por proyecto y registro de pagos"
          restricted={!isMasterAdmin}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                select fullWidth label="Proyecto"
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(asSelectValue(e.target.value))}
              >
                {projects.map((p) => (
                  <MenuItem key={p.id} value={asSelectValue(p.id)}>
                    {p.name}
                    <Chip
                      label={p.billing_status}
                      size="small"
                      sx={{
                        ml: 1,
                        height: 18,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        backgroundColor:
                          p.billing_status === 'active' ? tokens.color.status.active.bg
                          : p.billing_status === 'overdue' ? tokens.color.status.offline.bg
                          : tokens.color.status.suspended.bg,
                        color:
                          p.billing_status === 'active' ? tokens.color.status.active.text
                          : p.billing_status === 'overdue' ? tokens.color.status.offline.text
                          : tokens.color.status.suspended.text,
                      }}
                    />
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select fullWidth label="Estado"
                value={billingPatch.billing_status}
                onChange={(e) => setBillingPatch((p) => ({ ...p, billing_status: e.target.value }))}
                disabled={!isMasterAdmin}
              >
                <MenuItem value="active">Activo</MenuItem>
                <MenuItem value="suspended">Suspendido</MenuItem>
                <MenuItem value="overdue">Vencido</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <TextField
                fullWidth label="Motivo del cambio"
                value={billingPatch.reason}
                onChange={(e) => setBillingPatch((p) => ({ ...p, reason: e.target.value }))}
                disabled={!isMasterAdmin}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth type="date" label="Próximo cobro"
                InputLabelProps={{ shrink: true }}
                value={billingPatch.next_billing_date}
                onChange={(e) => setBillingPatch((p) => ({ ...p, next_billing_date: e.target.value }))}
                disabled={!isMasterAdmin}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 8 }} sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleUpdateBilling}
                disabled={!isMasterAdmin || !selectedProjectId}
              >
                Actualizar estado
              </Button>
            </Grid>

            <Grid size={12}>
              <Divider />
            </Grid>

            <Grid size={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>Registrar pago</Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth type="number" label="Monto"
                value={paymentPatch.amount}
                onChange={(e) => setPaymentPatch((p) => ({ ...p, amount: e.target.value }))}
                disabled={!isMasterAdmin}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth label="Moneda"
                value={paymentPatch.currency}
                onChange={(e) => setPaymentPatch((p) => ({ ...p, currency: e.target.value.toUpperCase() }))}
                disabled={!isMasterAdmin}
                helperText="USD, EUR..."
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth label="Notas del pago"
                value={paymentPatch.notes}
                onChange={(e) => setPaymentPatch((p) => ({ ...p, notes: e.target.value }))}
                disabled={!isMasterAdmin}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select fullWidth label="Activar automáticamente"
                value={paymentPatch.auto_activate ? 'yes' : 'no'}
                onChange={(e) => setPaymentPatch((p) => ({ ...p, auto_activate: e.target.value === 'yes' }))}
                disabled={!isMasterAdmin}
              >
                <MenuItem value="yes">Sí</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </TextField>
            </Grid>
            <Grid size={12}>
              <Button
                variant="contained"
                onClick={handleRegisterPayment}
                disabled={!isMasterAdmin || !selectedProjectId || !paymentPatch.amount}
              >
                Registrar pago
              </Button>
            </Grid>

            {/* Historial de facturación */}
            {billingHistory.length > 0 && (
              <Grid size={12}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="overline" sx={{ mb: 1.5, display: 'block' }}>
                  Historial de facturación
                </Typography>
                <Stack spacing={1}>
                  {billingHistory.slice(0, 15).map((ev) => (
                    <Box
                      key={ev.id}
                      sx={{
                        p: 1.5,
                        borderRadius: tokens.radius.md,
                        border: `1px solid ${tokens.color.border.default}`,
                        backgroundColor: tokens.color.surface.default,
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {ev.event_type}
                          {ev.new_status ? ` → ${ev.new_status}` : ''}
                        </Typography>
                        <Typography variant="caption" sx={{ color: tokens.color.ink.muted }}>
                          {new Date(ev.created_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: tokens.color.ink.muted }}>
                        por {ev.created_by_username || 'sistema'}
                        {ev.amount ? ` · ${ev.amount} ${ev.currency}` : ''}
                        {ev.notes ? ` · ${ev.notes}` : ''}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Grid>
            )}
          </Grid>
        </Section>
          </>
        )}

        {activeTab === 'history' && (
          <>
        {/* 7. Historial de tablet */}
        <Section icon={History} title="Historial de asignaciones por tablet">
          <Grid container spacing={2} alignItems="flex-end">
            <Grid size={{ xs: 12, md: 6 }}>{deviceSelector()}</Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Button variant="outlined" onClick={handleLoadHistory} disabled={!selectedDeviceId}>
                Cargar historial
              </Button>
            </Grid>
          </Grid>

          {history && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Stack direction="row" spacing={2}>
                {[
                  { label: 'Cambios de proyecto',     count: history.project?.length   || 0 },
                  { label: 'Cambios de admin',         count: history.admins?.length    || 0 },
                  { label: 'Cambios de usuario final', count: history.end_users?.length || 0 },
                ].map(({ label, count }) => (
                  <Box
                    key={label}
                    sx={{
                      flex: 1,
                      p: 2,
                      borderRadius: tokens.radius.md,
                      border: `1px solid ${tokens.color.border.default}`,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="h3" sx={{ fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.02em' }}>
                      {count}
                    </Typography>
                    <Typography variant="caption" sx={{ color: tokens.color.ink.muted }}>
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Section>
          </>
        )}
      </Stack>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default SaaSManagement;
